import { useState, useEffect } from 'react';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import Zoom from '@mui/material/Zoom';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { MdAdd as AddIcon, MdEdit as EditIcon, MdDelete as DeleteIcon, MdInfo as InfoIcon, MdRule as RuleIcon } from 'react-icons/md';
import BorderBlueButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderBlueButtonV2';
import AddRuleModal from './AddRuleModal';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import KaiziqResolveConnectionService from '@Helpers/URLHelpers/KaiziqResolveConnectionService';
import ErrorHandler from '@Root/ErrorHandler';
import './AssigneeMatrixForm.scss';

const cn = 'assigneeMatrixForm';

export default function AssigneeMatrixForm(props) {

    const [cursor, setCursor] = useState('auto');
    const [formFieldList, setFormFieldList] = useState([]);
    const [isEdited, setIsEdited] = useState(false);
    const [ruleTypes] = useState(['reporter', 'field']);
    const [fieldParameter, setFieldParameter] = useState([]);
    const [conditions] = useState(['EQUALS', 'NOT EQUALS', 'CONTAINS']);
    const [comparisonOperators] = useState(['EQUALS', 'GRATER THEN', 'LESS THEN']);
    const [property] = useState(['fullName', 'email', 'phoneNumber', 'domain']);
    const [users, setUsers] = useState([]);
    const [kaiziqResolveConnection, setKaiziqResolveConnection] = useState({});
    const [rules, setRules] = useState([]);
    const [snackBarOpen, setSnackBarOpen] = useState(false);
    const [snackBarType, setSnackBarType] = useState('success');
    const [snackBarMessage, setSnackBarMessage] = useState('');
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [showAddRuleModal, setShowAddRuleModal] = useState(false);
    const [showEditRuleModal, setShowEditRuleModal] = useState(false);
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
    const [tempModalRule, setTempModalRule] = useState(null);
    const [ruleToDelete, setRuleToDelete] = useState(null);
    const [validationError, setValidationError] = useState('');

    const { currentProjectId, getSimpleUsersByProjectId } = useReduxProject();
    const kaiziqResolveConnectionService = new KaiziqResolveConnectionService();
    const errorHandler = new ErrorHandler();

    const filteredUsers = users.filter((user) =>
        (user?.fullName || '').toLowerCase().includes(userSearchTerm.toLowerCase())
    );

    useEffect(() => { renderData(); }, []);

    const renderData = async () => {
        try {
            setCursor('wait');
            if (!!props.formFieldList) {
                let formFieldsTemp = props.formFieldList;
                let formIdList = props.formFieldIdList;
                let formFields = [];
                let fieldParameterList = [];
                for (let i = 0; i < formFieldsTemp.length; i++) {
                    let itemObj = formFieldsTemp[i];
                    if (formIdList.includes(itemObj.id)) {
                        itemObj = { ...itemObj, options: JSON.parse(itemObj.options) };
                        formFields.push(itemObj);
                        fieldParameterList.push({ id: itemObj.id, type: itemObj.type, title: itemObj.title });
                    }
                }
                setFieldParameter(fieldParameterList);
                setFormFieldList(formFields);
                await renderKaiziqResolveConnection();
                await renderUserListData();
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    };

    const renderUserListData = async () => {
        for (let i = 0; i < 10; i++) {
            try {
                const allUsers = await getSimpleUsersByProjectId(currentProjectId);
                setUsers(allUsers);
                return true;
            } catch (err) {
                errorHandler.logError('User Filtered Data', err);
                if (i === 9) { errorHandler.handleError(err); return false; }
            }
        }
    };

    const renderKaiziqResolveConnection = async () => {
        try {
            let obj = await kaiziqResolveConnectionService.getKaiziqResolveConnectionById(props.kaiziqResolveConnectionId);
            obj = JSON.parse(obj.body);
            obj = { ...obj, details: JSON.parse(obj.details) };
            setKaiziqResolveConnection(obj);
            setRules(obj.details?.assigneeRules ?? []);
        } catch (err) {
            errorHandler.handleError(err);
        }
    };

    const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const validateDomain = (domain) => /^[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}$/.test(domain);
    const validatePhoneNumber = (phone) => /^[\d\s\-+()]{7,}$/.test(phone);

    const validateByPropertyAndCondition = (property, condition, value) => {
        if (!value) return '';
        if (property === 'email') {
            if (condition === 'EQUALS' || condition === 'NOT EQUALS') {
                if (!validateEmail(value)) return 'Enter a full email address (e.g. user@example.com)';
            } else if (condition === 'DOMAIN') {
                if (!validateDomain(value)) return 'Enter a domain only (e.g. example.com)';
            }
            // CONTAINS → any substring, no format restriction
        }
        if (property === 'phoneNumber' && !validatePhoneNumber(value)) {
            return 'Please enter a valid phone number';
        }
        return '';
    };

    const saveRuleToDatabase = async (updatedRules) => {
        try {
            let details = kaiziqResolveConnection.details;
            details.assigneeRules = updatedRules;
            await kaiziqResolveConnectionService.updateKaiziqResolveConnectionDetails(
                kaiziqResolveConnection?.id, JSON.stringify(details)
            );
            setIsEdited(false);
        } catch (err) {
            errorHandler.handleError(err);
            setSnackBarMessage('Error in updating rules');
            setSnackBarType('error');
            setSnackBarOpen(true);
        }
    };

    const handleOpenAddRuleModal = () => {
        setTempModalRule({ id: Date.now(), type: 'reporter', property: '', assignee: 0, fieldId: 0, condition: '', paramValue: '' });
        setValidationError('');
        setShowAddRuleModal(true);
    };

    const handleCloseAddRuleModal = () => { setShowAddRuleModal(false); setTempModalRule(null); setValidationError(''); };
    const handleOpenEditRuleModal = (rule) => { setTempModalRule({ ...rule }); setValidationError(''); setShowEditRuleModal(true); };
    const handleCloseEditRuleModal = () => { setShowEditRuleModal(false); setTempModalRule(null); setValidationError(''); };
    const handleOpenDeleteConfirmModal = (ruleId) => { setRuleToDelete(ruleId); setShowDeleteConfirmModal(true); };
    const handleCloseDeleteConfirmModal = () => { setRuleToDelete(null); setShowDeleteConfirmModal(false); };

    const handleConfirmDelete = async () => {
        const updatedRules = rules.filter(r => r.id !== ruleToDelete);
        setRules(updatedRules);
        setShowDeleteConfirmModal(false);
        setRuleToDelete(null);
        showSnack('Rule deleted successfully', 'success');
        setIsEdited(true);
        await saveRuleToDatabase(updatedRules);
    };

    const handleAddRuleSave = async (newRule) => {
        if (!validateModalRule(newRule)) return;
        const updatedRules = [...rules, newRule];
        setRules(updatedRules);
        setShowAddRuleModal(false);
        setValidationError('');
        showSnack('Rule added successfully', 'success');
        setIsEdited(true);
        await saveRuleToDatabase(updatedRules);
    };

    const handleEditRuleSave = async (updatedRule) => {
        if (!validateModalRule(updatedRule)) return;
        const updatedRules = rules.map(r => r.id === updatedRule.id ? updatedRule : r);
        setRules(updatedRules);
        setShowEditRuleModal(false);
        setValidationError('');
        showSnack('Rule updated successfully', 'success');
        setIsEdited(true);
        await saveRuleToDatabase(updatedRules);
    };

    const validateModalRule = (rule) => {
        const error = validateByPropertyAndCondition(rule.property, rule.condition, rule.paramValue);
        if (error) { setValidationError(error); return false; }
        return true;
    };

    const handleModalRuleChange = (field, value) => {
        if (field === 'property') {
            setTempModalRule(r => ({ ...r, [field]: value, condition: '', paramValue: '' }));
        } else if (field === 'paramValue') {
            setTempModalRule(r => ({ ...r, [field]: value }));
            const error = validateByPropertyAndCondition(tempModalRule.property, tempModalRule.condition, value);
            setValidationError(error);
        } else {
            setTempModalRule(r => ({ ...r, [field]: value }));
        }
    };

    const handleModalRuleFieldChange = (fieldId) => {
        const selectedField = fieldParameter.find(f => f.id === fieldId);
        setTempModalRule(r => ({ ...r, fieldId, property: selectedField?.type || '', condition: '', paramValue: '' }));
    };

    const handleModalRuleUserChange = (userId) => {
        setTempModalRule(r => ({ ...r, assignee: userId }));
    };

    const showSnack = (message, type) => {
        setSnackBarMessage(message);
        setSnackBarType(type);
        setSnackBarOpen(true);
    };

    const handleSnackBarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackBarOpen(false);
    };

    const getRulesSummary = (rule) => {
        if (rule.type === 'reporter') {
            const propertyLabel = rule.property === 'fullName' ? 'Full Name' :
                                  rule.property === 'phoneNumber' ? 'Phone Number' :
                                  rule.property?.toUpperCase() || 'N/A';
            const assigneeName = users.find(u => u.id === rule.assignee)?.fullName || 'Unassigned';
            return (
                <span className={`${cn}__ruleSummary`}>
                    <span className={`${cn}__ruleSummary__source`}>Reporter</span>
                    <span className={`${cn}__ruleSummary__prop`}>{propertyLabel}</span>
                    <span className={`${cn}__ruleSummary__badge ${cn}__ruleSummary__badge--condition`}>
                        {rule.condition?.replace('_', ' ')}
                    </span>
                    <span className={`${cn}__ruleSummary__value`}>"{rule.paramValue}"</span>
                    <span className={`${cn}__ruleSummary__arrow`}>→</span>
                    <span className={`${cn}__ruleSummary__badge ${cn}__ruleSummary__badge--assignee`}>{assigneeName}</span>
                </span>
            );
        } else if (rule.type === 'field') {
            const field = formFieldList.find(f => f.id === rule.fieldId);
            const assigneeName = users.find(u => u.id === rule.assignee)?.fullName || 'Unassigned';
            return (
                <span className={`${cn}__ruleSummary`}>
                    <span className={`${cn}__ruleSummary__source`}>Field</span>
                    <span className={`${cn}__ruleSummary__prop`}>{field?.title || 'Select Field'}</span>
                    <span className={`${cn}__ruleSummary__badge ${cn}__ruleSummary__badge--condition`}>
                        {rule.condition?.replace('_', ' ')}
                    </span>
                    <span className={`${cn}__ruleSummary__value`}>{rule.paramValue}</span>
                    <span className={`${cn}__ruleSummary__arrow`}>→</span>
                    <span className={`${cn}__ruleSummary__badge ${cn}__ruleSummary__badge--assignee`}>{assigneeName}</span>
                </span>
            );
        }
        return 'Incomplete Rule';
    };

    const sharedModalProps = {
        rule: tempModalRule,
        onRuleChange: handleModalRuleChange,
        onFieldChange: handleModalRuleFieldChange,
        onUserChange: handleModalRuleUserChange,
        validationError,
        ruleTypes,
        conditions,
        comparisonOperators,
        property,
        fieldParameter,
        formFieldList,
        filteredUsers,
        userSearchTerm,
        setUserSearchTerm,
        saveDisabled: !tempModalRule?.assignee || !tempModalRule?.condition || !!validationError,
    };

    return (
        <div className={cn} style={{ cursor }}>

            {/* ── Header ───────────────────────────────────────────────────── */}
            <div className={`${cn}__header`}>
                <div className={`${cn}__header__left`}>
                    <div className={`${cn}__header__iconWrap`}>
                        <RuleIcon />
                    </div>
                    <div className={`${cn}__header__text`}>
                        <Badge color='error' variant='dot' invisible={!isEdited}>
                            <h2 className={`${cn}__header__title`}>Ticket Assignee Matrix</h2>
                        </Badge>
                        <p className={`${cn}__header__subtitle`}>
                            Rules are evaluated top-to-bottom — the last match decides the assignee.
                        </p>
                    </div>
                </div>
                <Tooltip
                    title={
                        <span>
                            Rules are evaluated from top to bottom.<br />
                            The last matching rule decides the assignee.
                        </span>
                    }
                    slots={{ transition: Zoom }}
                    arrow>
                    <span className={`${cn}__header__infoIcon`}>
                        <InfoIcon />
                    </span>
                </Tooltip>
            </div>

            {/* ── Rules list ────────────────────────────────────────────────── */}
            <div className={`${cn}__body`}>
                {rules.length === 0 ? (
                    <div className={`${cn}__empty`}>
                        <RuleIcon className={`${cn}__empty__icon`} />
                        <p className={`${cn}__empty__text`}>No rules configured yet.</p>
                        <p className={`${cn}__empty__hint`}>Add a rule to automatically assign tickets based on reporter or field values.</p>
                    </div>
                ) : (
                    <div className={`${cn}__rulesList`}>
                        {rules.map((rule, index) => (
                            <div className={`${cn}__ruleItem`} key={rule.id}>
                                <div className={`${cn}__ruleItem__index`}>{index + 1}</div>
                                <div className={`${cn}__ruleItem__summary`}>
                                    {getRulesSummary(rule)}
                                </div>
                                <div className={`${cn}__ruleItem__actions`}>
                                    <button
                                        className={`${cn}__ruleItem__btn ${cn}__ruleItem__btn--edit`}
                                        onClick={() => handleOpenEditRuleModal(rule)}
                                        title='Edit Rule'>
                                        <EditIcon />
                                    </button>
                                    <button
                                        className={`${cn}__ruleItem__btn ${cn}__ruleItem__btn--delete`}
                                        onClick={() => handleOpenDeleteConfirmModal(rule.id)}
                                        title='Delete Rule'>
                                        <DeleteIcon />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <button className={`${cn}__addBtn`} onClick={handleOpenAddRuleModal}>
                    <AddIcon className={`${cn}__addBtn__icon`} />
                    Add Rule
                </button>
            </div>

            {/* ── Add Rule Modal ────────────────────────────────────────────── */}
            <AddRuleModal
                open={showAddRuleModal}
                onClose={handleCloseAddRuleModal}
                onSave={handleAddRuleSave}
                title='Add New Rule'
                {...sharedModalProps}
            />

            {/* ── Edit Rule Modal ───────────────────────────────────────────── */}
            <AddRuleModal
                open={showEditRuleModal}
                onClose={handleCloseEditRuleModal}
                onSave={handleEditRuleSave}
                title='Edit Rule'
                {...sharedModalProps}
            />

            {/* ── Delete Confirmation Modal ─────────────────────────────────── */}
            <Dialog
                open={showDeleteConfirmModal}
                onClose={handleCloseDeleteConfirmModal}
                maxWidth='xs'
                fullWidth
                className={`${cn}__deleteDialog`}>
                <DialogTitle className={`${cn}__deleteDialog__title`}>
                    Delete Rule
                </DialogTitle>
                <DialogContent className={`${cn}__deleteDialog__content`}>
                    <p className={`${cn}__deleteDialog__text`}>
                        Are you sure you want to delete this rule? This action cannot be undone.
                    </p>
                </DialogContent>
                <DialogActions className={`${cn}__deleteDialog__actions`}>
                    <button className={`${cn}__cancelBtn`} onClick={handleCloseDeleteConfirmModal}>
                        Cancel
                    </button>
                    <BorderBlueButtonV2 text='Delete' onClick={handleConfirmDelete} />
                </DialogActions>
            </Dialog>

            {/* ── Snackbar ──────────────────────────────────────────────────── */}
            <Snackbar
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                open={snackBarOpen}
                autoHideDuration={5000}
                onClose={handleSnackBarClose}>
                <Alert onClose={handleSnackBarClose} severity={snackBarType} variant='filled' sx={{ width: '100%' }}>
                    {snackBarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
}
